// @ts-strict-ignore
import { Color } from '@coast/schemas';
import React, { Component, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { ScrollView, TextInput } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { Styles } from '@shared/components/form/styles';
import { MBSelectableDot } from '@shared/components/list';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2, MBTypographyStyles } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { AuditTagComponent } from '../../config';

type TagOption = MBAPI.WorkflowTemplate_components_AuditTagComponent_options;

export interface Props {
  tagOptions: AuditTagComponent['options'];
  tags: string[];
  isMultiselect: boolean;
  onSearch?(keyword: string): void;
  onChange?(selectedTags: string[]): void;
  onClose?(): void;
}

type ListItem = { value: TagOption; selected: boolean };

export function AuditTag({ tagOptions, tags, isMultiselect, onSearch, onChange, onClose }: Props) {
  const colors = useContext(ColorThemeContext);
  const [query, _setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const setQuery = useCallback(
    (keyword: string) => {
      _setQuery(keyword);
      onSearch?.(keyword);
    },
    [onSearch],
  );

  const matchesSearchString = (option: TagOption): boolean => {
    return option.label.toLowerCase().includes(query.toLowerCase());
  };

  const filteredItems = tagOptions?.filter(matchesSearchString).map((tagOption: TagOption) => ({
    value: tagOption,
    selected: tags?.some((selectedValue: string) => selectedValue === tagOption.value),
    archived: tagOption.archived,
  }));

  function newMultiselectSelections(newSelection: string, existingSelections: string[]): string[] {
    if (existingSelections.includes(newSelection)) {
      return existingSelections.filter(item => item !== newSelection);
    }
    return [newSelection].concat(existingSelections ?? []);
  }

  function newSingleSelectSelection(newSelection: string, existingSelections: string[]): string[] {
    if (existingSelections?.includes(newSelection)) {
      return [];
    }
    return [newSelection];
  }

  const toggleItem = useCallback(
    (value: TagOption, _component: Component) => {
      const newValues = isMultiselect
        ? newMultiselectSelections(value.value, tags ?? [])
        : newSingleSelectSelection(value.value, tags ?? []);
      onChange?.(newValues);

      if (!isMultiselect) {
        onClose();
      }
    },
    [isMultiselect, onChange, onClose, tags],
  );

  const renderListItem = useCallback(
    (listItem: ListItem): ReactNode => (
      <MBTouchableOpacity
        onPress={component => toggleItem(listItem.value, component)}
        key={listItem.value.value}
      >
        <View row centerVertically paddingVertical={0.5}>
          <View row flex={4} wrap shrinkWrap style={{ alignContent: 'flex-start' }}>
            <Tag
              color={listItem.value.backgroundColor as Color}
              textStyle={MBTypographyStyles.body2}
              label={listItem.value.label}
            />
          </View>
          <View flex style={{ alignItems: 'flex-end' }}>
            <MBSelectableDot selected={listItem.selected} radio={!isMultiselect} />
          </View>
        </View>
      </MBTouchableOpacity>
    ),
    [isMultiselect, toggleItem],
  );

  const searchInputRef = useRef<TextInput>(null);

  const onChangeSearchText = useCallback(
    (searchText: string) => {
      setQuery(searchText);
      setError(null);
    },
    [setQuery, setError],
  );

  return (
    <View paddingHorizontal={2} fill paddingBottom={1}>
      <MBView style={Styles.whiteBackground} paddingTop={2} paddingBottom={1}>
        <MBSearchBar
          forwardRef={searchInputRef}
          value={query}
          onChangeText={onChangeSearchText}
          returnKeyType="go"
          autoFocus={true}
        />
        {!!error && (
          <MBTextBody2 color={colors.error.foreground} style={{ padding: grid(1) }}>
            {error}
          </MBTextBody2>
        )}
      </MBView>
      <View fill>
        <ScrollView keyboardShouldPersistTaps="always">
          {filteredItems?.map(renderListItem)}
        </ScrollView>
      </View>
    </View>
  );
}
